import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import { Activity, Clock3, RefreshCcw, Rocket, Wallet } from "lucide-react";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { useDashboard } from "@/hooks/useDashboard";
import { ApiFetchLog } from "@/types";

const apiLogStatusLabels: Record<string, string> = {
  pending: "待機",
  running: "実行中",
  success: "成功",
  error: "失敗",
};

const apiLogStatusClasses: Record<string, string> = {
  pending: "bg-slate-100 text-slate-700",
  running: "bg-blue-100 text-blue-700",
  success: "bg-emerald-100 text-emerald-700",
  error: "bg-rose-100 text-rose-700",
};

const formatRelativeTime = (dateString?: string) => {
  if (!dateString) return "未実行";
  try {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: ja,
    });
  } catch {
    return "不明";
  }
};

const safeNumber = (value: unknown) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : 0;
};

const formatUsd = (value: number) => `$${value.toFixed(3)}`;

const formatApiLogStatus = (status: ApiFetchLog["status"]) =>
  apiLogStatusLabels[status] ?? status;

export const ActivityPage = () => {
  const { usage, recentTweets, isLoading, error } = useDashboard();
  const unitCostUsd = usage?.unit_cost_usd ?? 0.005;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">取得履歴</h1>
            <p className="text-gray-600">
              API取得ログと保存済みの最新投稿を一覧で確認します。
            </p>
          </div>
        </div>

        {error ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
            履歴の読み込みに失敗しました。しばらく待ってから再読み込みしてください。
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">本日のAPI呼び出し</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">
                  {safeNumber(usage?.api_calls_today).toLocaleString()}
                </p>
              </div>
              <Activity className="h-7 w-7 text-slate-400" />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">本日の取得投稿数</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">
                  {safeNumber(usage?.posts_fetched_today).toLocaleString()}
                </p>
              </div>
              <Rocket className="h-7 w-7 text-slate-400" />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">本日の同期回数</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">
                  {safeNumber(usage?.synced_today).toLocaleString()}
                </p>
              </div>
              <RefreshCcw className="h-7 w-7 text-slate-400" />
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">1ポスト単価</p>
                <p className="mt-1 text-2xl font-semibold text-slate-900">
                  {formatUsd(unitCostUsd)}
                </p>
              </div>
              <Wallet className="h-7 w-7 text-slate-400" />
            </div>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-lg font-medium text-gray-900">最近のAPI取得ログ</h3>
                <p className="text-sm text-gray-500">
                  取得件数と `1 post = ${formatUsd(unitCostUsd)}` の計算結果を確認します。
                </p>
              </div>
              <span className="text-sm text-gray-500">
                {usage?.recent_api_logs?.length ?? 0} 件
              </span>
            </div>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="h-28 animate-pulse rounded-xl bg-slate-100" />
                ))}
              </div>
            ) : !usage?.recent_api_logs?.length ? (
              <p className="text-sm text-gray-600">
                まだAPI取得ログがありません。Tracked Account の同期を実行するとここに表示されます。
              </p>
            ) : (
              <div className="space-y-3">
                {usage.recent_api_logs.map((log) => {
                  const fetchedCount = safeNumber(log.fetched_count);
                  const newCount = safeNumber(log.new_count);
                  const requestedCount = safeNumber(log.requested_count);
                  const estimatedCostUsd = safeNumber(log.estimated_cost_usd);

                  return (
                    <div
                      key={log.id}
                      className="rounded-xl border border-gray-200 px-4 py-3"
                    >
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-medium text-gray-900">
                              @{log.screen_name || "unknown"}
                            </p>
                            {log.display_name ? (
                              <span className="text-sm text-gray-500">{log.display_name}</span>
                            ) : null}
                            <span
                              className={`rounded-full px-2.5 py-1 text-xs font-medium ${apiLogStatusClasses[log.status] ?? apiLogStatusClasses.pending}`}
                            >
                              {formatApiLogStatus(log.status)}
                            </span>
                            <span className="text-xs uppercase tracking-wide text-gray-400">
                              {log.trigger_type}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                            <span>取得 {fetchedCount.toLocaleString()} 件</span>
                            <span>新規保存 {newCount.toLocaleString()} 件</span>
                            <span>要求上限 {requestedCount.toLocaleString()} 件</span>
                            <span>{formatRelativeTime(log.completed_at || log.started_at)}</span>
                          </div>
                          <p className="text-sm text-gray-700">
                            {fetchedCount.toLocaleString()} posts × {formatUsd(unitCostUsd)} ={" "}
                            <span className="font-semibold text-gray-900">
                              {formatUsd(estimatedCostUsd)}
                            </span>
                          </p>
                          {log.error_message ? (
                            <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                              {log.error_message}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-medium text-gray-900">最新取得投稿</h3>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, index) => (
                  <div key={index} className="h-32 animate-pulse rounded-xl bg-slate-100" />
                ))}
              </div>
            ) : recentTweets.length === 0 ? (
              <p className="text-sm text-gray-600">
                まだ保存済み投稿がありません。Tracked Account の手動同期または定期同期を実行してください。
              </p>
            ) : (
              <div className="space-y-4">
                {recentTweets.map((tweet) => (
                  <article key={tweet.id} className="rounded-xl border border-gray-200 p-4">
                    <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900">@{tweet.user_screen_name}</span>
                      <span className="text-gray-500">{tweet.user_name}</span>
                      <span className="text-gray-400">•</span>
                      <span className="text-gray-500">{formatRelativeTime(tweet.created_at)}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">
                      {tweet.text}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                      <span>返信 {safeNumber(tweet.reply_count)}</span>
                      <span>再投稿 {safeNumber(tweet.retweet_count)}</span>
                      <span>いいね {safeNumber(tweet.favorite_count)}</span>
                      <span>引用 {safeNumber(tweet.quote_count)}</span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
          最終同期: <span className="font-medium text-slate-900">{formatRelativeTime(usage?.last_sync_at)}</span>
          <span className="mx-3 text-slate-300">|</span>
          <Clock3 className="mr-1 inline h-4 w-4 align-text-bottom text-slate-400" />
          このページは 30 秒ごとに自動更新されます。
        </div>
      </div>
    </DashboardLayout>
  );
};
